import type { FixMessageTag } from "../domain";
import {
  normalizeVersionKey,
  getComponentsByName,
  getGroupsByCountTag,
  getImplicitGroupsByCountTag,
  getImplicitGroupsByDelimTag,
  getKnownTagSet,
  type GroupDef,
  type ImplicitGroupDef,
} from "./thesaurus-loader";
import type {
  DataDictionary,
  ComponentDef,
  ItemRef,
  MessageDef,
} from "./data-dictionary";
import {
  createComponentsIndex,
  createDataDictionaryFieldsIndex,
  createMessagesByType,
} from "./data-dictionary";

// Public surface (Milestone 1 subset)
export type NodeKind =
  | "Header"
  | "Body"
  | "Trailer"
  | "Component"
  | "Group"
  | "Field"
  | "XmlData";

export type Severity = "info" | "warning" | "error";

export interface Diagnostic {
  code: string;
  message: string;
  severity: Severity;
  tagIndex?: number;
  tag?: number;
  context?: string;
}

export interface StructureNodeBase {
  kind: NodeKind;
  name?: string;
  schemaId?: string;
  startIndex: number;
  endIndex: number;
}

export interface FieldNode extends StructureNodeBase {
  kind: "Field";
  tag: number;
  value: string;
}

export interface XmlDataNode extends StructureNodeBase {
  kind: "XmlData";
  lengthTag: number;
  dataTag: number;
  length: number;
  data: string;
}

export interface ComponentNode extends StructureNodeBase {
  kind: "Component";
  type?: "Block" | "ImplicitBlock";
  children: StructureNode[];
}

export interface GroupNode extends StructureNodeBase {
  kind: "Group";
  type?:
    | "BlockRepeating"
    | "ImplicitBlockRepeating"
    | "OptimisedImplicitBlockRepeating";
  delimTag?: number;
  expectedCount?: number;
  entries: ComponentNode[];
}

export type StructureNode = FieldNode | XmlDataNode | ComponentNode | GroupNode;

export interface MessageStructure {
  version: string; // e.g., "FIX.4.4" or "FIXT.1.1+FIX5.0SP2"
  msgType: string; // e.g., "D"
  // For future milestones; currently empty arrays
  header: StructureNode[];
  body: StructureNode[];
  trailer: StructureNode[];
  diagnostics: Diagnostic[];
  // Milestone 1: reflect resolved template identity (placeholder until full schema wiring)
  templateId?: string;
}

export interface DecodeOptions {
  tolerance?: "strict" | "relaxed";
  keepUnknownFields?: boolean;
}

const findTag = (
  tags: FixMessageTag[],
  tag: number,
): { value: string | null; index: number } | null => {
  const idx = tags.findIndex((t) => t.tag === tag);
  if (idx === -1) return null;
  return { value: tags[idx].value, index: idx };
};

const normalizeAppVersion = (v: string): string => {
  // Expect values like "FIX.4.4", "FIX.5.0SP2", case-insensitive
  const s = v.trim().toUpperCase();
  // Basic guardrails; accept only FIX x.y with optional SPn
  if (!s.startsWith("FIX")) return s;
  return s.replace(/\s+/g, "");
};

/**
 * Milestone 1: Dictionary selection and template resolution
 */
const decodeCache = new WeakMap<
  FixMessageTag[],
  Map<DataDictionary | undefined, MessageStructure>
>();

export const decodeStructure = (
  tags: FixMessageTag[],
  fixDictionary?: DataDictionary,
  options?: DecodeOptions,
): MessageStructure => {
  // Use cache if available for default options
  if (!options) {
    let dictCache = decodeCache.get(tags);
    if (dictCache) {
      const cached = dictCache.get(fixDictionary);
      if (cached) return cached;
    } else {
      dictCache = new Map();
      decodeCache.set(tags, dictCache);
    }

    const result = decodeStructureInternal(tags, fixDictionary, options);
    dictCache.set(fixDictionary, result);
    return result;
  }

  return decodeStructureInternal(tags, fixDictionary, options);
};

const decodeStructureInternal = (
  tags: FixMessageTag[],
  fixDictionary?: DataDictionary,
  options?: DecodeOptions,
): MessageStructure => {
  // Extract core system/header tags
  let version = "";
  let msgType = "";
  let templateId: string | undefined = undefined;

  const beginString = findTag(tags, 8)?.value ?? null; // BeginString
  const msgTypeTag = findTag(tags, 35)?.value ?? null; // MsgType
  const applVerId = findTag(tags, 1128)?.value ?? null; // ApplVerID
  const defaultApplVerId = findTag(tags, 1137)?.value ?? null; // DefaultApplVerID

  if (msgTypeTag) {
    msgType = msgTypeTag;
    // Until we wire real schemas, use MsgType as a placeholder template id
    templateId = msgTypeTag;
  }

  if (beginString) {
    const bs = beginString.toUpperCase();
    if (bs === "FIXT.1.1") {
      // FIXT transport; need application dictionary from ApplVerID/DefaultApplVerID
      const appVerRaw = applVerId ?? defaultApplVerId;
      if (appVerRaw) {
        const appVer = normalizeAppVersion(appVerRaw);
        version = `FIXT.1.1+${appVer}`;
      } else {
        // If missing, leave unknown application version explicitly
        version = "FIXT.1.1+UNKNOWN";
      }
    } else {
      // Regular FIX 4.x or 5.0
      version = bs;
    }
  } else {
    version = "UNKNOWN";
  }

  // Thesaurus-driven parsing
  const diagnostics: Diagnostic[] = [];
  const headerNodes: StructureNode[] = [];
  const trailerNodes: StructureNode[] = [];
  let bodyNodes: StructureNode[] = [];

  const versionKey = normalizeVersionKey(version);
  const knownTagsBase = versionKey
    ? getKnownTagSet(versionKey)
    : new Set<number>();
  const groupsByCountBase = versionKey
    ? getGroupsByCountTag(versionKey)
    : new Map<number, GroupDef>();
  const implicitGroupsByCount = versionKey
    ? getImplicitGroupsByCountTag(versionKey)
    : new Map<number, ImplicitGroupDef>();
  const implicitGroupsByDelim = versionKey
    ? getImplicitGroupsByDelimTag(versionKey)
    : new Map<number, ImplicitGroupDef>();
  const componentsByName = versionKey
    ? getComponentsByName(versionKey)
    : undefined;

  // Merge built-in thesaurus with optional external FIX Data Dictionary (custom fields/groups)
  const knownTags = new Set<number>(knownTagsBase);
  const groupsByCount = new Map<number, GroupDef>(groupsByCountBase);

  if (fixDictionary) {
    // Ensure index is built
    if (!fixDictionary.fieldsIndex) {
      try {
        fixDictionary.fieldsIndex = createDataDictionaryFieldsIndex(
          fixDictionary.fields || [],
        );
      } catch {
        // ignore index build errors, fall back to built-in only
      }
    }

    // Add custom field tags to knownTags to avoid UNEXPECTED_TAG diagnostics
    if (Array.isArray(fixDictionary.fields)) {
      for (const f of fixDictionary.fields) {
        if (typeof f.tag === "number" && Number.isFinite(f.tag)) {
          knownTags.add(f.tag);
        }
      }
    }
  }

  // Build header/trailer tag sets from thesaurus when available
  const headerTagSet = new Set<number>();
  const trailerTagSet = new Set<number>();
  if (componentsByName) {
    const hdr = componentsByName.get("StandardHeader");
    if (hdr) {
      for (const e of hdr.entries) {
        if (/^\d+$/.test(e.tagText)) headerTagSet.add(parseInt(e.tagText, 10));
      }
    }
    const trl = componentsByName.get("StandardTrailer");
    if (trl) {
      for (const e of trl.entries) {
        if (/^\d+$/.test(e.tagText)) trailerTagSet.add(parseInt(e.tagText, 10));
      }
    }
  }

  // Parse header from the beginning using the header tag set (if known)
  let cursor = 0;
  if (headerTagSet.size > 0) {
    while (cursor < tags.length) {
      const tg = tags[cursor]?.tag;
      if (tg == null) break;
      if (!headerTagSet.has(tg)) break;
      // Emit header field node
      headerNodes.push({
        kind: "Field",
        tag: tg,
        value: String(tags[cursor].value ?? ""),
        startIndex: cursor,
        endIndex: cursor,
      } as FieldNode);
      cursor += 1;
    }
  } else {
    // Fallback: stop header after MsgType (35) if present
    const mt = findTag(tags, 35);
    if (mt) cursor = mt.index + 1;
    else cursor = 0;
  }
  const bodyStartIndex = cursor;

  // Identify trailer start: prefer CheckSum (10) index if present
  let trailerStartIndex = tags.length;
  const chkIdx = findTag(tags, 10)?.index ?? -1;
  if (chkIdx !== -1 && chkIdx >= bodyStartIndex) {
    trailerStartIndex = chkIdx;
  } else if (trailerTagSet.size > 0) {
    for (let i = bodyStartIndex; i < tags.length; i++) {
      if (trailerTagSet.has(tags[i].tag)) {
        trailerStartIndex = i;
        break;
      }
    }
  }
  const bodyEndIndex = trailerStartIndex;

  // Parse body. When the dictionary has a <message> entry matching MsgType (35), use a
  // schema-driven walker that inlines <component> refs and recurses into nested <group>s.
  // Otherwise parse with thesaurus-only knowledge (header/trailer extraction + the
  // built-in FIX thesaurus's explicit/implicit groups). A dictionary without <messages>
  // contributes only its custom tag IDs (to silence UNEXPECTED_TAG diagnostics).
  let resolvedSchema: SchemaInstr[] | null = null;
  if (fixDictionary && msgType) {
    if (
      !fixDictionary.messagesByType &&
      Array.isArray(fixDictionary.messages)
    ) {
      fixDictionary.messagesByType = createMessagesByType(
        fixDictionary.messages,
      );
    }
    if (!fixDictionary.componentsIndex) {
      fixDictionary.componentsIndex = createComponentsIndex(
        fixDictionary.components || [],
      );
    }
    const messageDef = fixDictionary.messagesByType?.get(msgType);
    const labelToTag = fixDictionary.fieldsIndex?.labelToTag;
    if (messageDef && labelToTag) {
      resolvedSchema = resolveMessageSchema(
        messageDef,
        labelToTag,
        fixDictionary.componentsIndex,
        diagnostics,
      );
    }
  }

  bodyNodes = resolvedSchema
    ? parseBodyWithSchema(
        tags,
        bodyStartIndex,
        bodyEndIndex,
        resolvedSchema,
        knownTags,
        options,
        diagnostics,
      )
    : parseBodyWithGroups(
        tags,
        bodyStartIndex,
        bodyEndIndex,
        groupsByCount,
        implicitGroupsByCount,
        implicitGroupsByDelim,
        knownTags,
        options,
        diagnostics,
      );

  // Parse trailer fields if present
  if (trailerStartIndex < tags.length) {
    for (let i = trailerStartIndex; i < tags.length; i++) {
      const tg = tags[i].tag;
      if (trailerTagSet.size === 0 || trailerTagSet.has(tg)) {
        trailerNodes.push({
          kind: "Field",
          tag: tg,
          value: String(tags[i].value ?? ""),
          startIndex: i,
          endIndex: i,
        } as FieldNode);
      }
    }
  }

  return {
    version,
    msgType,
    header: headerNodes,
    body: bodyNodes,
    trailer: trailerNodes,
    diagnostics,
    templateId,
  };
};

// ===== Milestone 2 internal schema + parser (provisional) =====

interface SchemaItemField {
  kind: "Field";
  tag: number;
  required?: boolean;
}

interface SchemaItemGroup {
  kind: "Group";
  // Explicit repeating group (NumInGroup based)
  countTag: number; // NumInGroup tag id
  delimTag?: number; // delimiter/first field of each entry (e.g., 448 for Parties)
  entryBlock: SchemaBlock; // schema for one entry (a Block)
  required?: boolean;
}

type SchemaItem = SchemaItemField | SchemaItemGroup; // future kinds to be added in later milestones

interface SchemaBlock {
  items: SchemaItem[];
}

// Thesaurus-driven body parser with explicit repeating groups (NumInGroup) support
function parseBodyWithGroups(
  tags: FixMessageTag[],
  startIndex: number,
  endIndex: number,
  groupsByCount: Map<number, GroupDef>,
  implicitGroupsByCount: Map<number, ImplicitGroupDef>,
  implicitGroupsByDelim: Map<number, ImplicitGroupDef>,
  knownTags: Set<number>,
  options: DecodeOptions | undefined,
  diagnostics: Diagnostic[],
): StructureNode[] {
  const nodes: StructureNode[] = [];
  const tolerance: "strict" | "relaxed" = options?.tolerance ?? "relaxed";
  let cursor = startIndex;

  while (cursor < endIndex) {
    const field = tags[cursor];
    if (!field) break;
    const tag = field.tag;

    // 1) Explicit repeating group (NumInGroup + entries)
    const groupDef = groupsByCount.get(tag);
    if (groupDef) {
      const start = cursor;
      // Read expected count
      const raw = String(field.value ?? "0");
      const parsed = parseInt(raw, 10);
      const expected = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
      cursor += 1; // consume count tag

      const entries: ComponentNode[] = [];

      const entryFieldSet = new Set<number>(groupDef.entryFieldTags);
      const parseOneEntry = (): ComponentNode | null => {
        if (cursor >= endIndex) return null;
        if (tags[cursor]?.tag !== groupDef.delimTag) return null; // must start with delimiter
        const entryStart = cursor;
        const entryChildren: StructureNode[] = [];
        while (cursor < endIndex) {
          const t = tags[cursor];
          if (!t) break;
          const tg = t.tag;
          if (tg === groupDef.delimTag && cursor !== entryStart) break; // next entry
          if (entryFieldSet.has(tg)) {
            entryChildren.push({
              kind: "Field",
              tag: tg,
              value: String(t.value ?? ""),
              startIndex: cursor,
              endIndex: cursor,
            } as FieldNode);
            cursor += 1;
            continue;
          }
          break; // field not part of this entry
        }
        if (entryChildren.length === 0) return null;
        return {
          kind: "Component",
          type: "Block",
          children: entryChildren,
          startIndex: entryChildren[0].startIndex,
          endIndex: entryChildren[entryChildren.length - 1].endIndex,
        } as ComponentNode;
      };

      for (let i = 0; i < expected; i++) {
        const e = parseOneEntry();
        if (!e) break;
        entries.push(e);
      }

      while (true) {
        const e = parseOneEntry();
        if (!e) break;
        entries.push(e);
      }

      if (entries.length !== expected) {
        diagnostics.push({
          code: "COUNT_MISMATCH",
          message: `Group count mismatch for ${groupDef.countTag}: declared ${expected}, parsed ${entries.length}`,
          severity: tolerance === "strict" ? "error" : "warning",
          tagIndex: start,
          tag: groupDef.countTag,
        });
      }

      nodes.push({
        kind: "Group",
        type: "BlockRepeating",
        delimTag: groupDef.delimTag,
        expectedCount: expected,
        entries,
        startIndex: start,
        endIndex: Math.max(
          start,
          entries[entries.length - 1]?.endIndex ?? start,
        ),
      } as GroupNode);
      continue;
    }

    // 2) Implicit repeating group with a count field (e.g., 267 followed by N entries starting with 269)
    const impByCount = implicitGroupsByCount.get(tag);
    if (impByCount) {
      const start = cursor;
      // expected count from current field
      const raw = String(field.value ?? "0");
      const parsed = parseInt(raw, 10);
      const expected = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
      cursor += 1; // consume count tag

      const entryFieldSet = new Set<number>(impByCount.entryFieldTags);
      const entries: ComponentNode[] = [];
      const parseOneEntry = (): ComponentNode | null => {
        if (cursor >= endIndex) return null;
        if (tags[cursor]?.tag !== impByCount.delimTag) return null; // must start with delimiter
        const entryStart = cursor;
        const entryChildren: StructureNode[] = [];
        while (cursor < endIndex) {
          const t = tags[cursor];
          if (!t) break;
          const tg = t.tag;
          if (tg === impByCount.delimTag && cursor !== entryStart) break; // next entry
          if (entryFieldSet.has(tg)) {
            entryChildren.push({
              kind: "Field",
              tag: tg,
              value: String(t.value ?? ""),
              startIndex: cursor,
              endIndex: cursor,
            } as FieldNode);
            cursor += 1;
            continue;
          }
          break;
        }
        if (entryChildren.length === 0) return null;
        return {
          kind: "Component",
          type: "Block",
          children: entryChildren,
          startIndex: entryChildren[0].startIndex,
          endIndex: entryChildren[entryChildren.length - 1].endIndex,
        } as ComponentNode;
      };

      for (let i = 0; i < expected; i++) {
        const e = parseOneEntry();
        if (!e) break;
        entries.push(e);
      }

      while (true) {
        const e = parseOneEntry();
        if (!e) break;
        entries.push(e);
      }

      if (entries.length !== expected) {
        diagnostics.push({
          code: "COUNT_MISMATCH",
          message: `Group count mismatch for ${impByCount.countTag}: declared ${expected}, parsed ${entries.length}`,
          severity: tolerance === "strict" ? "error" : "warning",
          tagIndex: start,
          tag: impByCount.countTag,
        });
      }

      nodes.push({
        kind: "Group",
        type: "ImplicitBlockRepeating",
        delimTag: impByCount.delimTag,
        expectedCount: expected,
        entries,
        startIndex: start,
        endIndex: Math.max(
          start,
          entries[entries.length - 1]?.endIndex ?? start,
        ),
      } as GroupNode);
      continue;
    }

    // 3) Implicit repeating group starting at delimiter (no count consumed at this point)
    const impByDelim = implicitGroupsByDelim.get(tag);
    if (impByDelim) {
      const start = cursor;
      const entryFieldSet = new Set<number>(impByDelim.entryFieldTags);
      const entries: ComponentNode[] = [];

      const parseOneEntry = (): ComponentNode | null => {
        if (cursor >= endIndex) return null;
        if (tags[cursor]?.tag !== impByDelim.delimTag) return null; // must start with delimiter
        const entryStart = cursor;
        const entryChildren: StructureNode[] = [];
        while (cursor < endIndex) {
          const t = tags[cursor];
          if (!t) break;
          const tg = t.tag;
          if (tg === impByDelim.delimTag && cursor !== entryStart) break; // next entry
          if (entryFieldSet.has(tg)) {
            entryChildren.push({
              kind: "Field",
              tag: tg,
              value: String(t.value ?? ""),
              startIndex: cursor,
              endIndex: cursor,
            } as FieldNode);
            cursor += 1;
            continue;
          }
          break;
        }
        if (entryChildren.length === 0) return null;
        return {
          kind: "Component",
          type: "Block",
          children: entryChildren,
          startIndex: entryChildren[0].startIndex,
          endIndex: entryChildren[entryChildren.length - 1].endIndex,
        } as ComponentNode;
      };

      while (true) {
        const e = parseOneEntry();
        if (!e) break;
        entries.push(e);
      }

      if (entries.length > 0) {
        nodes.push({
          kind: "Group",
          type: "ImplicitBlockRepeating",
          delimTag: impByDelim.delimTag,
          entries,
          startIndex: start,
          endIndex: Math.max(
            start,
            entries[entries.length - 1]?.endIndex ?? start,
          ),
        } as GroupNode);
        continue;
      }
      // Fallback to field if we could not parse any entry (defensive)
    }

    // Regular field in body
    if (!knownTags.has(tag)) {
      diagnostics.push({
        code: "UNEXPECTED_TAG",
        message: `Unexpected tag ${tag} in body`,
        severity: tolerance === "strict" ? "error" : "warning",
        tagIndex: cursor,
        tag,
      });
    }

    nodes.push({
      kind: "Field",
      tag,
      value: String(field.value ?? ""),
      startIndex: cursor,
      endIndex: cursor,
    } as FieldNode);

    cursor += 1;
  }

  return nodes;
}

// ===== Schema-driven body parser (dictionary <messages>-aware) =====

// Runtime schema items: resolved from a MessageDef by inlining component refs and
// expanding nested groups. Component boundaries are preserved via open/close markers
// so the body parser can emit ComponentNode wrappers.
type SchemaInstr =
  | { kind: "field"; tag: number; required: boolean }
  | {
      kind: "group";
      countTag: number;
      delimTag: number;
      entry: SchemaInstr[];
      required: boolean;
      name: string;
    }
  | { kind: "component-open"; name: string; required: boolean }
  | { kind: "component-close"; name: string };

function findFirstFieldTag(
  items: ItemRef[] | undefined,
  labelToTag: Map<string, number>,
  componentsIndex: Map<string, ComponentDef> | undefined,
  visited: Set<string>,
): number | undefined {
  if (!items) return undefined;
  for (const it of items) {
    if (it.kind === "field") {
      const tg = labelToTag.get(it.name);
      if (typeof tg === "number") return tg;
    } else if (it.kind === "component") {
      if (visited.has(it.name)) continue;
      visited.add(it.name);
      const target = componentsIndex?.get(it.name);
      if (target) {
        const found = findFirstFieldTag(
          target.items,
          labelToTag,
          componentsIndex,
          visited,
        );
        if (typeof found === "number") return found;
      }
    } else if (it.kind === "group") {
      // The count tag of a nested group, if it stands as the very first item, is itself
      // a valid delim (rare). Otherwise we keep scanning.
      const tg = labelToTag.get(it.group.name);
      if (typeof tg === "number") return tg;
    }
  }
  return undefined;
}

function buildSchemaFromItems(
  items: ItemRef[],
  labelToTag: Map<string, number>,
  componentsIndex: Map<string, ComponentDef> | undefined,
  diagnostics: Diagnostic[],
  visiting: Set<string>,
  out: SchemaInstr[],
): void {
  for (const it of items) {
    if (it.kind === "field") {
      const tag = labelToTag.get(it.name);
      if (typeof tag !== "number") {
        diagnostics.push({
          code: "UNRESOLVED_REFERENCE",
          message: `Field "${it.name}" referenced in dictionary but missing from <fields>`,
          severity: "warning",
        });
        continue;
      }
      out.push({ kind: "field", tag, required: !!it.required });
    } else if (it.kind === "component") {
      if (visiting.has(it.name)) {
        diagnostics.push({
          code: "UNRESOLVED_REFERENCE",
          message: `Circular component reference detected: "${it.name}"`,
          severity: "warning",
        });
        continue;
      }
      const target = componentsIndex?.get(it.name);
      if (!target) {
        diagnostics.push({
          code: "UNRESOLVED_REFERENCE",
          message: `Component "${it.name}" referenced but not defined`,
          severity: "warning",
        });
        continue;
      }
      visiting.add(it.name);
      out.push({
        kind: "component-open",
        name: it.name,
        required: !!it.required,
      });
      buildSchemaFromItems(
        target.items,
        labelToTag,
        componentsIndex,
        diagnostics,
        visiting,
        out,
      );
      out.push({ kind: "component-close", name: it.name });
      visiting.delete(it.name);
    } else if (it.kind === "group") {
      const g = it.group;
      const countTag = labelToTag.get(g.name);
      const delimTag = findFirstFieldTag(
        g.items,
        labelToTag,
        componentsIndex,
        new Set<string>(),
      );
      if (typeof countTag !== "number" || typeof delimTag !== "number") {
        diagnostics.push({
          code: "UNRESOLVED_REFERENCE",
          message: `Group "${g.name}" could not be resolved (missing count or delim field in <fields>)`,
          severity: "warning",
        });
        continue;
      }
      const entry: SchemaInstr[] = [];
      buildSchemaFromItems(
        g.items,
        labelToTag,
        componentsIndex,
        diagnostics,
        visiting,
        entry,
      );
      out.push({
        kind: "group",
        countTag,
        delimTag,
        entry,
        required: !!g.required,
        name: g.name,
      });
    }
  }
}

function resolveMessageSchema(
  messageDef: MessageDef,
  labelToTag: Map<string, number>,
  componentsIndex: Map<string, ComponentDef> | undefined,
  diagnostics: Diagnostic[],
): SchemaInstr[] {
  const out: SchemaInstr[] = [];
  buildSchemaFromItems(
    messageDef.items,
    labelToTag,
    componentsIndex,
    diagnostics,
    new Set<string>(),
    out,
  );
  return out;
}

interface ComponentFrame {
  name: string;
  required: boolean;
  diagStart: number;
}

// Walk from openIdx forward, balancing component-open/close, to find the index
// of the component-close that pairs with the open at openIdx.
function findMatchingCompClose(items: SchemaInstr[], openIdx: number): number {
  let depth = 1;
  for (let j = openIdx + 1; j < items.length; j++) {
    const k = items[j].kind;
    if (k === "component-open") depth += 1;
    else if (k === "component-close") {
      depth -= 1;
      if (depth === 0) return j;
    }
  }
  return items.length; // malformed schema; treat as "open to end"
}

// Returns true if any field/group item inside the half-open range
// (openIdx, closeIdx) accepts the given tag. Used at component-open to decide
// whether an optional component should be entered transactionally.
function componentAcceptsTag(
  items: SchemaInstr[],
  openIdx: number,
  closeIdx: number,
  tag: number,
): boolean {
  for (let j = openIdx + 1; j < closeIdx; j++) {
    const it = items[j];
    if (it.kind === "field" && it.tag === tag) return true;
    if (it.kind === "group" && it.countTag === tag) return true;
  }
  return false;
}

// Returns true if any later schema item (in this block) accepts the given tag.
// "Accepts" means a field item whose tag matches, or a group item whose count tag matches.
function lookaheadHasMatch(
  items: SchemaInstr[],
  from: number,
  tag: number,
): boolean {
  for (let j = from; j < items.length; j++) {
    const it = items[j];
    if (it.kind === "field" && it.tag === tag) return true;
    if (it.kind === "group" && it.countTag === tag) return true;
  }
  return false;
}

function parseBodyWithSchema(
  tags: FixMessageTag[],
  startIndex: number,
  endIndex: number,
  schema: SchemaInstr[],
  knownTags: Set<number>,
  options: DecodeOptions | undefined,
  diagnostics: Diagnostic[],
): StructureNode[] {
  const tolerance: "strict" | "relaxed" = options?.tolerance ?? "relaxed";
  // Stack of "collecting buckets" — top-level is index 0; component frames push.
  const buckets: StructureNode[][] = [[]];
  const compFrames: ComponentFrame[] = [];
  let cursor = startIndex;

  const emit = (node: StructureNode) => {
    buckets[buckets.length - 1].push(node);
  };

  const closeComponentFrame = () => {
    const children = buckets.pop() as StructureNode[];
    const frame = compFrames.pop();
    if (!frame) return;
    if (children.length === 0) {
      if (!frame.required) {
        // Optional component produced no children — drop diagnostics emitted
        // while parsing its inner items (MISSING_REQUIRED for inner required
        // fields). The component is absent.
        diagnostics.length = frame.diagStart;
      } else {
        diagnostics.push({
          code: "MISSING_REQUIRED",
          message: `Required component "${frame.name}" is missing`,
          severity: tolerance === "strict" ? "error" : "info",
        });
      }
      return;
    }
    emit({
      kind: "Component",
      type: "Block",
      name: frame.name,
      children,
      startIndex: children[0].startIndex,
      endIndex: children[children.length - 1].endIndex,
    } as ComponentNode);
  };

  // Run a (sub-)schema starting at cursor.
  // When inGroupEntry=true, an unmatched cursor tag terminates the run (the
  // outer group's loop decides what to do); when false, an unmatched tag is
  // emitted as a plain Field with an UNEXPECTED_TAG diagnostic and parsing
  // continues against the same schema item — so one stray tag doesn't break
  // recognition of later expected fields/groups.
  const runSchema = (items: SchemaInstr[], inGroupEntry: boolean): void => {
    // Frame depth on entry so we can unwind any component frames opened during
    // this call when returning early (prevents leaking buckets/frames into
    // the caller's scope, e.g. parseOneEntry).
    const startCompFrames = compFrames.length;
    const unwindLocalFrames = () => {
      while (compFrames.length > startCompFrames) closeComponentFrame();
    };
    let i = 0;
    while (i < items.length && cursor < endIndex) {
      const inst = items[i];

      if (inst.kind === "component-open") {
        // Transactional gating: an OPTIONAL component is entered only if the
        // current cursor tag is something it can actually consume. Otherwise
        // we skip past its matching component-close without pushing a frame.
        // This prevents optional components from grabbing fields that belong
        // at message level (or later) when their schema doesn't accept them.
        if (!inst.required && cursor < endIndex) {
          const closeIdx = findMatchingCompClose(items, i);
          if (!componentAcceptsTag(items, i, closeIdx, tags[cursor].tag)) {
            i = closeIdx + 1;
            continue;
          }
        }
        buckets.push([]);
        compFrames.push({
          name: inst.name,
          required: inst.required,
          diagStart: diagnostics.length,
        });
        i += 1;
        continue;
      }
      if (inst.kind === "component-close") {
        closeComponentFrame();
        i += 1;
        continue;
      }

      const curTag = tags[cursor]?.tag;
      const expectedTag = inst.kind === "field" ? inst.tag : inst.countTag;

      if (curTag === expectedTag) {
        if (inst.kind === "field") {
          emit({
            kind: "Field",
            tag: curTag,
            value: String(tags[cursor].value ?? ""),
            startIndex: cursor,
            endIndex: cursor,
          } as FieldNode);
          cursor += 1;
          i += 1;
          continue;
        }
        // Group
        const groupStart = cursor;
        const raw = String(tags[cursor].value ?? "0");
        const parsed = parseInt(raw, 10);
        const expected = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
        cursor += 1; // consume count
        const entries: ComponentNode[] = [];
        const parseOneEntry = (): ComponentNode | null => {
          if (cursor >= endIndex) return null;
          if (tags[cursor]?.tag !== inst.delimTag) return null;
          buckets.push([]);
          runSchema(inst.entry, true);
          const children = buckets.pop() as StructureNode[];
          if (children.length === 0) return null;
          return {
            kind: "Component",
            type: "Block",
            children,
            startIndex: children[0].startIndex,
            endIndex: children[children.length - 1].endIndex,
          } as ComponentNode;
        };
        for (let k = 0; k < expected; k++) {
          const e = parseOneEntry();
          if (!e) break;
          entries.push(e);
        }
        while (cursor < endIndex && tags[cursor]?.tag === inst.delimTag) {
          const e = parseOneEntry();
          if (!e) break;
          entries.push(e);
        }
        if (entries.length !== expected) {
          diagnostics.push({
            code: "COUNT_MISMATCH",
            message: `Group count mismatch for ${inst.countTag}: declared ${expected}, parsed ${entries.length}`,
            severity: tolerance === "strict" ? "error" : "warning",
            tagIndex: groupStart,
            tag: inst.countTag,
          });
        }
        emit({
          kind: "Group",
          type: "BlockRepeating",
          delimTag: inst.delimTag,
          expectedCount: expected,
          entries,
          startIndex: groupStart,
          endIndex: Math.max(
            groupStart,
            entries[entries.length - 1]?.endIndex ?? groupStart,
          ),
        } as GroupNode);
        i += 1;
        continue;
      }

      // Current cursor tag doesn't match this schema item. Two cases:
      //   (a) curTag matches some LATER item in this block → the current item
      //       is the one that's actually missing; advance schema (skip it).
      //   (b) curTag matches nothing in the remaining schema → it's either an
      //       out-of-order/unknown tag in the body (top level) or the boundary
      //       of an outer construct we shouldn't consume (group entry).
      if (lookaheadHasMatch(items, i + 1, curTag)) {
        if (inst.required) {
          diagnostics.push({
            code: "MISSING_REQUIRED",
            message: `Missing required tag ${expectedTag}`,
            severity: tolerance === "strict" ? "error" : "info",
            tag: expectedTag,
          });
        }
        i += 1;
        continue;
      }

      if (inGroupEntry) {
        // Hand control back to the outer group loop. Unwind any component
        // frames we opened during this call so the caller's bucket stack is
        // intact.
        unwindLocalFrames();
        return;
      }

      // Top level: emit the stray tag as a plain Field into the current
      // scope (= the active component's bucket if open, else the root body).
      // Order is preserved naturally: the stray appears between the fields
      // that bracket it in the wire.
      const t = tags[cursor];
      if (!knownTags.has(t.tag)) {
        diagnostics.push({
          code: "UNEXPECTED_TAG",
          message: `Unexpected tag ${t.tag} in body`,
          severity: tolerance === "strict" ? "error" : "warning",
          tagIndex: cursor,
          tag: t.tag,
        });
      }
      emit({
        kind: "Field",
        tag: t.tag,
        value: String(t.value ?? ""),
        startIndex: cursor,
        endIndex: cursor,
      } as FieldNode);
      cursor += 1;
    }

    // Schema not yet exhausted — process trailing markers and diagnose
    // remaining required items.
    while (i < items.length) {
      const inst = items[i];
      if (inst.kind === "component-open") {
        buckets.push([]);
        compFrames.push({
          name: inst.name,
          required: inst.required,
          diagStart: diagnostics.length,
        });
      } else if (inst.kind === "component-close") {
        closeComponentFrame();
      } else if (inst.required) {
        const missingTag = inst.kind === "field" ? inst.tag : inst.countTag;
        diagnostics.push({
          code: "MISSING_REQUIRED",
          message: `Missing required tag ${missingTag}`,
          severity: tolerance === "strict" ? "error" : "info",
          tag: missingTag,
        });
      }
      i += 1;
    }
  };

  runSchema(schema, false);

  // Anything left in the body that the schema didn't consume — emit as plain fields.
  while (cursor < endIndex) {
    const t = tags[cursor];
    if (!knownTags.has(t.tag)) {
      diagnostics.push({
        code: "UNEXPECTED_TAG",
        message: `Unexpected tag ${t.tag} in body`,
        severity: tolerance === "strict" ? "error" : "warning",
        tagIndex: cursor,
        tag: t.tag,
      });
    }
    emit({
      kind: "Field",
      tag: t.tag,
      value: String(t.value ?? ""),
      startIndex: cursor,
      endIndex: cursor,
    } as FieldNode);
    cursor += 1;
  }

  return buckets[0];
}
